/**
 * Pure validator for checking pending documents on an onboarding-aanmelding.
 *
 * Used by:
 * - linked-items.component.ts (documenten module) → DetailsService.setNotice → notices() chip in the detail header
 * (tabs.component.ts reads DetailsService.noticesCountForKeyPaths for the "Documenten (N)" tab-counter; it does not call this validator)
 *
 * Module-ID guard (9e760e7a-…) must be applied by the caller.
 */

/**
 * Storage shape for documenten.entry.file (verified against test env entries).
 * The file is uploaded via the upload component and stored as a doc-reference.
 * It can be null/undefined when no upload has happened, or an object with at
 * least documentName once a file is attached.
 */
export interface DocumentFileRef {
  documentId?: string;
  documentName?: string;
  blobReference?: string;
  blobUrl?: string | null;
  createdDateTime?: string;
}

export interface LinkedDocument {
  /**
   * documenten.entry.status — custom-select with taxonomy, datatype: object.
   * Stored as a single option-object {id,name,slug,selectedInput}, NOT a string.
   * The taxonomy slugs are pending / approved / rejected.
   */
  status?: { slug?: string } | null;
  /**
   * documenten.entry.file — uploaded document reference. Null/undefined when no
   * file has been uploaded; populated with at least documentName once a file
   * is attached. Empty object {} also counts as "no file".
   */
  file?: DocumentFileRef | null;
}

export interface PendingDocumentsResult {
  count: number;
  message: string;
}

export type PendingDocumentsValidatorResult = {
  pendingDocuments: PendingDocumentsResult;
} | null;

/**
 * THE "pending document" rule, in one place: a document still needs action when
 * - its status slug is NOT 'approved', OR
 * - it has no uploaded file (no documentId AND no documentName).
 *
 * Exported so every consumer derives from the same predicate — `aanmeldingDocumentsPendingValidator`
 * below counts with it, and `FormlyLinkedItemsComponent` lists the pending documents' names for
 * the detail-header notice chip with it. Two copies of this rule once let the chip's count and its
 * hover-card list disagree the moment one of them changed.
 */
export function isPendingLinkedDocument(doc: LinkedDocument): boolean {
  const noFile = !doc.file?.documentId && !doc.file?.documentName;
  const notApproved = doc.status?.slug !== 'approved';
  return noFile || notApproved;
}

/**
 * Returns null when all linked docs are fully approved and have a file.
 * Returns a result object when one or more docs still need action (see `isPendingLinkedDocument`).
 */
export function aanmeldingDocumentsPendingValidator(
  linkedDocs: readonly LinkedDocument[]
): PendingDocumentsValidatorResult {
  if (!linkedDocs?.length) return null;

  const pendingCount = linkedDocs.filter(isPendingLinkedDocument).length;

  if (pendingCount === 0) return null;

  return {
    pendingDocuments: {
      count: pendingCount,
      message: `${pendingCount} document(en) wachten nog op upload of beoordeling`,
    },
  };
}
